import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function AdminLocations() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({
    queryKey: ["locations-all"],
    queryFn: async () => (await api.get("/locations")).data,
  });
  const [form, setForm] = useState({ name: "", code: "", type: "godown", parent_id: "" });
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/locations", { ...form, parent_id: form.parent_id || null });
      toast.success("Location created");
      setForm({ name: "", code: "", type: "godown", parent_id: "" });
      qc.invalidateQueries({ queryKey: ["locations-all"] });
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  const toggle = async (l) => {
    try {
      await api.patch(`/locations/${l.id}`, { active: !l.active });
      qc.invalidateQueries({ queryKey: ["locations-all"] });
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-locations">
      <div>
        <div className="data-label">Master</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Locations</h1>
      </div>
      <div className="bg-white border border-border rounded-sm p-4 grid md:grid-cols-5 gap-2 items-end">
        <input className="field-sm" placeholder="Name" value={form.name}
               onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="loc-name" />
        <input className="field-sm font-mono" placeholder="Code" value={form.code}
               onChange={(e) => setForm(f => ({ ...f, code: e.target.value }))} data-testid="loc-code" />
        <select className="field-sm" value={form.type}
                onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))} data-testid="loc-type">
          <option value="godown">godown</option>
          <option value="office">office</option>
          <option value="room">room</option>
          <option value="rack">rack</option>
          <option value="shelf">shelf</option>
        </select>
        <select className="field-sm" value={form.parent_id}
                onChange={(e) => setForm(f => ({ ...f, parent_id: e.target.value }))} data-testid="loc-parent">
          <option value="">(no parent)</option>
          {data.map(l => <option key={l.id} value={l.id}>{l.code} · {l.name}</option>)}
        </select>
        <button onClick={create} disabled={busy || !form.name || !form.code} className="btn-primary"
                data-testid="loc-create">
          <Plus className="w-4 h-4 mr-1" /> Add
        </button>
      </div>
      <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="locations-list">
        {data.map(l => (
          <div key={l.id} className="p-3 flex items-center gap-3">
            <span className="badge-flat bg-primary/10 text-primary">{l.type}</span>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{l.name}</div>
              <div className="container-code text-muted-foreground">{l.code}</div>
            </div>
            {!l.active && <span className="badge-flat bg-muted text-muted-foreground">disabled</span>}
            <button onClick={() => toggle(l)} className="btn-ghost text-xs"
                    data-testid={`loc-toggle-${l.code}`}>{l.active ? "Disable" : "Enable"}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
